import { PASSWORD_MIN_LENGTH } from '@chukua/common';
import { Button, Form, Input } from 'antd';
import { Lock } from 'lucide-react';

interface PasswordFormValues {
  password: string;
  confirm: string;
}

interface PasswordFormProps {
  submitLabel: string;
  submitting: boolean;
  onSave: (password: string) => void;
  onCancel: () => void;
}

/** A new password typed twice. Long rather than complex: the server checks the same minimum. */
export function PasswordForm({ submitLabel, submitting, onSave, onCancel }: PasswordFormProps) {
  return (
    <Form<PasswordFormValues>
      className="mt-4 max-w-sm"
      layout="vertical"
      requiredMark={false}
      onFinish={(values) => onSave(values.password)}
      disabled={submitting}
    >
      <Form.Item
        name="password"
        label="New password"
        extra={`At least ${PASSWORD_MIN_LENGTH} characters.`}
        rules={[
          { required: true, message: 'Enter a password' },
          { min: PASSWORD_MIN_LENGTH, message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters` },
        ]}
      >
        <Input.Password prefix={<Lock className="size-4 text-muted" />} autoComplete="new-password" autoFocus />
      </Form.Item>
      <Form.Item
        name="confirm"
        label="Type it again"
        dependencies={['password']}
        rules={[
          { required: true, message: 'Type the password again' },
          ({ getFieldValue }) => ({
            validator: (_, value) =>
              !value || value === getFieldValue('password') ? Promise.resolve() : Promise.reject(new Error("The passwords don't match")),
          }),
        ]}
      >
        <Input.Password prefix={<Lock className="size-4 text-muted" />} autoComplete="new-password" />
      </Form.Item>
      <div className="flex gap-2">
        <Button type="primary" htmlType="submit" loading={submitting}>
          {submitLabel}
        </Button>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </div>
    </Form>
  );
}
